import React, { useMemo } from 'react';
import { ReprocessDialog } from './ReprocessDialog';
import { DeleteRecordDialog } from './DeleteRecordDialog';
import { TagManagementDialog } from './TagManagementDialog';
import type { Tag } from '@/integrations/supabase/types';
import type { DialogType } from '@/hooks/useRecordDialogs';

interface RecordData {
  id: string;
  name: string;
  templateId: string;
  tags?: Tag[];
}

interface DeleteRecord {
  mutate: (id: string) => void;
  isPending: boolean;
}

interface DialogsState {
  reprocess: boolean;
  delete: boolean;
  tagManagement: boolean;
  openDialog: (dialog: DialogType) => void;
  closeDialog: (dialog: DialogType) => void;
}

interface RecordHandlers {
  handleReprocessSuccess: (processedText: string) => void;
  handleDeleteConfirm: () => void;
  handleTagsUpdated: (tags: Tag[]) => void;
  handleRefetchRecord: () => Promise<void>;
}

export interface RecordDialogsProps {
  record: RecordData;
  localRecord: RecordData | null;
  dialogs: DialogsState;
  handlers: RecordHandlers;
  deleteRecord: DeleteRecord;
}

export const RecordDialogs: React.FC<RecordDialogsProps> = React.memo(({
  record,
  localRecord,
  dialogs,
  handlers,
  deleteRecord,
}) => {
  const initialTags = useMemo(() => 
    (localRecord?.tags || record.tags) ?? [], 
    [localRecord?.tags, record.tags]
  );

  return (
    <>
      {/* Reprocess Dialog */}
      {dialogs.reprocess && (
        <ReprocessDialog
          data-testid="reprocess-dialog"
          data-record-id={record.id}
          data-record-name={record.name}
          data-template-id={record.templateId}
          open={dialogs.reprocess}
          onClose={() => dialogs.closeDialog('reprocess')}
          recordId={record.id}
          recordName={record.name}
          currentTemplateId={record.templateId}
          onSuccess={handlers.handleReprocessSuccess}
          onRefetchNeeded={handlers.handleRefetchRecord}
        />
      )}

      {/* Delete Record Dialog */}
      {dialogs.delete && (
        <DeleteRecordDialog
          data-testid="delete-dialog"
          data-record-name={record.name}
          data-is-deleting={deleteRecord.isPending.toString()}
          open={dialogs.delete}
          onClose={() => dialogs.closeDialog('delete')}
          recordName={record.name}
          onConfirm={handlers.handleDeleteConfirm}
          isDeleting={deleteRecord.isPending}
        />
      )}

      {/* Tag Management Dialog */}
      {dialogs.tagManagement && (
        <TagManagementDialog
          data-testid="tag-management-dialog"
          data-record-id={record.id}
          data-initial-tags-count={initialTags.length.toString()}
          open={dialogs.tagManagement}
          onClose={() => dialogs.closeDialog('tagManagement')}
          recordId={record.id}
          initialTags={initialTags}
          onTagsUpdated={handlers.handleTagsUpdated}
        />
      )}
    </>
  );
});